import React, { useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';

const AudioRecorder = ({ onSendAudio, maxDuration = 60 }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [duration, setDuration] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    setIsRecording(false);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      setAudioBlob(null);
      setDuration(0);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);

      timerRef.current = setInterval(() => {
        setDuration(prev => {
          if (prev >= maxDuration - 1) {
            stopRecording();
            return maxDuration;
          }

          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Erro ao iniciar gravacao:', error);
    }
  };

  const sendAudio = async () => {
    if (!audioBlob) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Audio = reader.result.split(',')[1];
      onSendAudio({
        audioData: base64Audio,
        mimeType: 'audio/webm',
        duration: duration
      });
      cancelRecording();
    };
    reader.readAsDataURL(audioBlob);
  };

  const cancelRecording = () => {
    setAudioBlob(null);
    setDuration(0);
    chunksRef.current = [];
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const actionButtonSx = {
    width: 40,
    height: 40,
    minWidth: 40,
    padding: 0,
    color: '#fff',
    flex: '0 0 40px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, transform 0.2s ease',
    '&:hover': {
      transform: 'scale(1.04)',
    },
    '& .MuiSvgIcon-root': {
      fontSize: 22,
      pointerEvents: 'none',
    },
  };

  const chipSx = {
    height: 24,
    minWidth: 42,
    padding: '0 7px',
    borderRadius: 12,
    fontSize: 11,
    lineHeight: '24px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    flex: '0 0 auto',
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', zIndex: 1, flexShrink: 0, minWidth: 0 }}>
      {!isRecording && !audioBlob && (
        <IconButton
          aria-label="Gravar audio"
          onClick={startRecording}
          sx={{
            ...actionButtonSx,
            backgroundColor: 'var(--mexx-green-primary, #8BC34A)',
            '&:hover': {
              ...actionButtonSx['&:hover'],
              backgroundColor: '#689F38',
            },
          }}
        >
          <MicIcon />
        </IconButton>
      )}

      {isRecording && (
        <>
          <IconButton
            aria-label="Parar gravacao"
            onClick={stopRecording}
            sx={{
              ...actionButtonSx,
              backgroundColor: '#d32f2f',
              '&:hover': {
                ...actionButtonSx['&:hover'],
                backgroundColor: '#b71c1c',
              },
            }}
          >
            <StopIcon />
          </IconButton>
          <Box component="span" sx={{
            ...chipSx,
            backgroundColor: '#ffebee',
            color: '#d32f2f',
            border: '1px solid #d32f2f'
          }}>
            <Box component="span" sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#d32f2f',
              animation: 'audioRecorderPulse 1.5s infinite',
              flex: '0 0 8px',
            }} />
            {formatDuration(duration)}
          </Box>
        </>
      )}

      {audioBlob && !isRecording && (
        <>
          <IconButton
            aria-label="Enviar audio"
            onClick={sendAudio}
            sx={{
              ...actionButtonSx,
              backgroundColor: '#2e7d32',
              '&:hover': {
                ...actionButtonSx['&:hover'],
                backgroundColor: '#1b5e20',
              },
            }}
          >
            <SendIcon />
          </IconButton>
          <IconButton
            aria-label="Cancelar audio"
            onClick={cancelRecording}
            sx={{
              ...actionButtonSx,
              backgroundColor: '#757575',
              '&:hover': {
                ...actionButtonSx['&:hover'],
                backgroundColor: '#616161',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box component="span" sx={{
            ...chipSx,
            backgroundColor: 'transparent',
            color: '#666',
            border: '1px solid #ddd'
          }}>
            {formatDuration(duration)}
          </Box>
        </>
      )}

      <style>{`
        @keyframes audioRecorderPulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

export default AudioRecorder;
